<script lang="ts">
/**
 * Show a tooltip when the mouse hover an element.
 *
 *
 * Simple tooltip:
 * ```vue
 * <Tooltip tooltip="Tooltip Content">
 *   <button>Button</button>
 * </Tooltip>
 * ```
 *
 * Complex tooltip:
 * ```vue
 * <Tooltip>
 *   <button>Button</button>
 *   <template #tooltip>
 *      <div> <!-- Content --> </div>
 *   </template>
 * </Tooltip>
 * ```
 *
 *
 * With a button that can be disabled:
 * ```vue
 * <Tooltip tooltip="Content">
 *   <TooltipRef> <!-- <span class="inline-block" tabindex="0"></span> also work -->
 *     <button disabled>Button</button>
 *   </TooltipRef>
 * </Tooltip>
 * ```
 */
export default {};

export interface TooltipProps extends PlacementProps, DeprecatedPlacementProps {
	disabled?: boolean;
	tooltip?: string;
	followCursor?: boolean;
	offset?: number;
}
</script>

<script setup lang="ts">
import { watchImmediate } from "@vueuse/core";
import {
	TooltipArrow,
	TooltipContent,
	TooltipPortal,
	TooltipRoot,
	TooltipTrigger,
	useForwardExpose,
} from "reka-ui";
import { cloneVNode, computed, mergeProps, nextTick, ref, useAttrs, useSlots, warn } from "vue";
import { useIsMobile } from "../../composables/is-mobile";
import {
	convertDeprecatedPlacement,
	type DeprecatedPlacementProps,
	type PlacementProps,
} from "../../lib/placement";
import { normalizeVNode } from "../../lib/vnode";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<TooltipProps>(), {
	disabled: undefined,
	followCursor: undefined,
	offset: 0,
});

const open = defineModel<boolean>("open");

const placement = computed<PlacementProps>(() => {
	if (props.side || props.align) {
		return { side: props.side, align: props.align };
	}

	return convertDeprecatedPlacement(props.placement ?? "bottom");
});

defineSlots<{
	default(): void;
	tooltip(): void;
}>();

const slots = useSlots();

const anchor = ref({
	x: -1,
	y: -1,
});

const position = computed(() => {
	if (!props.followCursor) {
		return undefined;
	}

	const ref = {
		getBoundingClientRect: () =>
			({
				width: 0,
				height: 0,
				left: anchor.value.x,
				right: anchor.value.x,
				top: anchor.value.y,
				bottom: anchor.value.y,
				...anchor.value,
			}) as DOMRect,
	};

	// Trigger one time so the computed watch the anchor ref
	ref.getBoundingClientRect();

	return ref;
});

function updateAnchor({ clientX, clientY }: MouseEvent) {
	anchor.value = {
		x: clientX,
		y: clientY,
	};
}
watchImmediate(
	() => props.followCursor,
	(follow, __, cleanup) => {
		if (!follow) {
			return;
		}

		document.addEventListener("mousemove", updateAnchor);
		cleanup(() => {
			document.removeEventListener("mousemove", updateAnchor);
		});
	}
);

const attrs = useAttrs();

const isMobile = useIsMobile();
const triggerIsButton = ref(false);
const mobileEvents = computed(() => {
	if (!isMobile.value || triggerIsButton.value) {
		return {};
	}

	return {
		onBlur: () => {
			open.value = false;
		},
		tabindex: 0,
		onFocus: () => {
			open.value = true;
		},
	};
});

function createTrigger() {
	if (props.followCursor) {
		return null;
	}

	const childs = normalizeVNode(slots.default?.());
	if (childs == null || childs.length === 0) {
		return;
	}

	if (childs.length > 1 && __DEV__) {
		warn(
			"[UI] Only one root element is supported for <Tooltip />. Only the first element will be rendered"
		);
	}

	const trigger = childs[0];

	if ("disabled" in (trigger.props || {}) && __DEV__) {
		warn(
			"[UI] Disabled button will not show tooltip. Please wrap the button with a span or with <TriggerRef />"
		);
	}

	const node = cloneVNode(
		trigger,
		mergeProps(attrs, {
			...(trigger.props || {}),
			...mobileEvents.value,
		})
	);

	return node;
}

const { forwardRef, currentRef } = useForwardExpose();

const stop = watchImmediate(currentRef, () => {
	if (currentRef.value == null) {
		return;
	}

	nextTick(() => stop());

	if (!("$el" in currentRef.value)) {
		return;
	}

	const el = currentRef.value.$el;

	if (!(el instanceof HTMLElement)) {
		return;
	}

	if (
		el instanceof HTMLButtonElement ||
		el instanceof HTMLAnchorElement ||
		el.querySelector("button") != null ||
		el.querySelector("a") != null
	) {
		triggerIsButton.value = true;
	}
});
</script>

<template>
	<TooltipRoot
		v-model:open="open"
		:defaultOpen="open"
		:disabled="props.disabled"
		:disableClosingTrigger="!triggerIsButton"
	>
		<TooltipTrigger v-if="position != null" :ref="forwardRef" :reference="position" />
		<TooltipTrigger v-else :ref="forwardRef" asChild>
			<component :is="createTrigger()" />
		</TooltipTrigger>

		<TooltipPortal>
			<TooltipContent
				class="popover max-w-sm px-3 py-1.5"
				:side="placement.side"
				:align="placement.align"
				:sideOffset="offset"
				avoidCollisions
				positionStrategy="fixed"
				updatePositionStrategy="optimized"
			>
				<slot name="tooltip">
					{{ tooltip }}
				</slot>
				<TooltipArrow class="popover-arrow" />
			</TooltipContent>
		</TooltipPortal>
	</TooltipRoot>
</template>
